<template>
  <div class="App">
    <ReconnectingBanner v-if="!hasFatalRenderError" />
    <RouterView v-if="!hasFatalRenderError"/>

    <!-- Last-resort error screen: renders when a child component's render/lifecycle
         throws and errorCaptured intercepts it. This lives OUTSIDE RouterView so it
         displays even when the route component is completely broken. Intentionally
         dependency-free (no i18n, no store, no OverlayError) to minimize failure risk. -->
    <div v-if="hasFatalRenderError" class="App__fatal-error" data-testid="app-fatal-error">
      <div class="App__fatal-error-content">
        <div class="App__fatal-error-icon">!</div>
        <h1 class="App__fatal-error-title">Something went wrong</h1>
        <p class="App__fatal-error-message">The kiosk encountered an error and will restart automatically.</p>
        <button class="App__fatal-error-button" @click="reloadPage">Restart Now</button>
      </div>
    </div>
  </div>
</template>


<script>
import { useAppLifecycle } from '@/app/runtime/useAppLifecycle';
import { useKioskBootstrap } from '@/app/runtime/useKioskBootstrap';
import ReconnectingBanner from '@/components/system/ReconnectingBanner.vue';
import { reloadWithCacheBust } from '@/services/DeploymentVersion';
import { sentryService } from '@/services/telemetry/sentry';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';

export default {
  components: { ReconnectingBanner },
  setup() {
    const appRuntime = useAppLifecycle();
    const kioskRuntime = useKioskBootstrap({
      boundHandlers: appRuntime.boundHandlers,
      ui: appRuntime.ui,
    });

    return {
      ui: appRuntime.ui,
      _boundHandlers: appRuntime.boundHandlers,
      _tabHiddenTimestamp: appRuntime.tabHiddenTimestamp,
      _cleanupAppRuntime: appRuntime.cleanupApp,
      _initializeAppRuntime: appRuntime.initializeApp,
      _initializeKioskServicesRuntime: kioskRuntime.initializeKioskServices,
    };
  },
  data() {
    return {
      hasFatalRenderError: false,
      _fatalReloadTimerId: null,
    };
  },

  /**
   * Vue error boundary — intercepts errors from ALL descendant components
   * during render, lifecycle hooks, and watchers. Without this, render errors
   * propagate to app.config.errorHandler which can only log (no UI recovery).
   *
   * Returns false to prevent error propagation — keeps the App component alive
   * so the fallback error screen renders instead of a white screen.
   */
  errorCaptured(err, component, info) {
    const componentName = component?.$options?.name || 'Unknown';
    // biome-ignore lint/suspicious/noConsole: preserve kiosk support diagnostics.
    console.error(`[App] errorCaptured from ${componentName} (${info}):`, err);

    try {
      sentryService.captureException(err, {
        tags: {
          error_type: 'vue_render_error',
          component: componentName,
          lifecycle_info: info,
          severity: 'critical',
        },
        extra: {
          component_name: componentName,
          lifecycle_info: info,
          route: window.location.pathname,
        },
      });
    } catch (_e) {
      // Sentry failure must not prevent error recovery
    }

    this.hasFatalRenderError = true;

    if (!this._fatalReloadTimerId) {
      this._fatalReloadTimerId = setTimeout(() => {
        try {
          sentryService.flush(2000);
        } catch (_e) {
          /* best effort */
        }
        reloadWithCacheBust();
      }, 10_000);
    }

    return false;
  },

  mounted() {
    try {
      this._initializeApp();
    } catch (e) {
      // biome-ignore lint/suspicious/noConsole: preserve kiosk support diagnostics.
      console.error(
        '[App] CRITICAL: mounted() threw, kiosk in degraded mode:',
        e
      );
      try {
        sentryService.captureException(e, {
          tags: { error_type: 'app_mount_failure', severity: 'critical' },
        });
      } catch (_sentryErr) {
        // Sentry itself failed -- nothing more we can do
      }
    }
  },

  beforeUnmount() {
    if (this._fatalReloadTimerId) {
      clearTimeout(this._fatalReloadTimerId);
      this._fatalReloadTimerId = null;
    }

    this._cleanupAppRuntime();
  },

  methods: {
    reloadPage() {
      try {
        sentryService.flush(2000);
      } catch (_e) {
        /* best effort */
      }
      reloadWithCacheBust();
    },

    _initializeApp() {
      this._initializeAppRuntime(
        () => exposeToGlobalScope(this),
        () => this._initializeKioskServices()
      );
    },

    _initializeKioskServices() {
      this._initializeKioskServicesRuntime();
    },
  },
};
</script>


<style scoped>
  /* Fatal error overlay — intentionally uses plain CSS (no SCSS, no variables)
     to render correctly even when the CSS pipeline is partially broken.
     Dark background with centered white text, similar to kiosk error theme. */
  .App__fatal-error {
    position: fixed;
    inset: 0;
    z-index: 99999;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #0a0a0a;
    color: #ffffff;
    font-family: system-ui, -apple-system, sans-serif;
  }

  .App__fatal-error-content {
    text-align: center;
    max-width: 500px;
    padding: 2rem;
  }

  .App__fatal-error-icon {
    width: 80px;
    height: 80px;
    margin: 0 auto 1.5rem;
    border-radius: 50%;
    border: 3px solid #ef4444;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 40px;
    font-weight: bold;
    color: #ef4444;
  }

  .App__fatal-error-title {
    font-size: 1.75rem;
    font-weight: 600;
    margin: 0 0 0.75rem;
  }

  .App__fatal-error-message {
    font-size: 1.1rem;
    opacity: 0.7;
    margin: 0 0 2rem;
    line-height: 1.5;
  }

  .App__fatal-error-button {
    background: #2563eb;
    color: #ffffff;
    border: none;
    border-radius: 8px;
    padding: 0.85rem 2rem;
    font-size: 1.1rem;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;
  }

  .App__fatal-error-button:hover {
    background: #1d4ed8;
  }

  .App__fatal-error-button:active {
    background: #1e40af;
  }
</style>
